import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import axios from "../utils/axios";
import {
  initializeLeadTracking,
  getStoredLeadSource,
  clearLeadSource,
  formatLeadSource,
} from "../utils/leadTracking";

// Unified Registration Page for all user types
const LeadRegistrationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadSourceData, setLeadSourceData] = useState(null);

  // Get registration type from URL params (lead, customer, staff) - defaults to lead
  const registrationType = searchParams.get("type") || "lead";
  const isLeadRegistration = registrationType === "lead";
  const isStaffRegistration = registrationType === "staff";

  // Initialize lead tracking on component mount
  useEffect(() => {
    const sourceData = initializeLeadTracking();
    setLeadSourceData(sourceData);
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      role: isStaffRegistration
        ? ""
        : isLeadRegistration
        ? undefined
        : "customer",
    },
  });

  const password = watch("password");

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      let response;

      if (isLeadRegistration) {
        // Lead registration - no password required
        // Only send fields that are needed for lead registration
        const { name, email, phone, whatsapp } = data;

        // Get the latest lead source data
        const currentSourceData = getStoredLeadSource() || leadSourceData;

        response = await axios.post("/api/leads/register", {
          name,
          email,
          phone,
          whatsapp,
          source: currentSourceData?.source || "direct",
          subSource: currentSourceData?.subSource,
          campaignId: currentSourceData?.campaignId,
          referralCode: currentSourceData?.referralCode,
          utmData: currentSourceData?.utmData,
          trackingData: currentSourceData,
        });

        // Clear lead source data after successful registration
        clearLeadSource();

        toast.success(
          "Registration successful! Check your email and WhatsApp for meeting details."
        );
        reset();

        // Redirect to thank you page
        navigate("/thank-you", {
          state: {
            message: "Registration successful!",
            leadId: response.data.leadId,
            sourceInfo: currentSourceData,
          },
        });
      } else {
        // User registration (customer or staff)
        // Remove confirmPassword from the data sent to backend
        const { confirmPassword, ...userData } = data;
        response = await axios.post("/api/auth/register", userData);

        toast.success(`Welcome to TamilSandhai, ${response.data.user.name}!`);

        // Redirect based on registration type
        if (isStaffRegistration) {
          navigate("/login", {
            state: { message: "Staff registration successful! Please login." },
          });
        } else {
          navigate("/", {
            state: {
              message: "Registration successful! Welcome to TamilSandhai!",
            },
          });
        }
      }
    } catch (error) {
      console.error("Registration error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPageConfig = () => {
    switch (registrationType) {
      case "lead":
        return {
          title: "Claim Your FREE Sales Consultation",
          subtitle:
            "Join our exclusive 3-10 minute video call to discover how our products and services can transform your business. Limited spots available!",
          buttonText: "🚀 Claim Your FREE Consultation Now",
          showSocialBadge: true,
          requirePassword: false,
          gradientFrom: "from-blue-50",
          gradientTo: "to-indigo-100",
        };
      case "staff":
        return {
          title: "Staff Registration",
          subtitle: "Join our team and access your specialized dashboard",
          buttonText: "Create Staff Account",
          showSocialBadge: false,
          requirePassword: true,
          gradientFrom: "from-indigo-50",
          gradientTo: "to-purple-100",
        };
      default: // customer
        return {
          title: "Customer Registration",
          subtitle:
            "Create your account to start shopping and accessing our services",
          buttonText: "Create Customer Account",
          showSocialBadge: false,
          requirePassword: true,
          gradientFrom: "from-green-50",
          gradientTo: "to-blue-100",
        };
    }
  };

  const config = getPageConfig();

  return (
    <div
      className={`min-h-screen py-12 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8`}
    >
      <div className="mx-auto">
        <div className="text-center mb-8">
          {/* {config.showSocialBadge && (
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-4">
              🎯 Special Offer from Facebook • Instagram • YouTube
            </div>
          )} */}
          <h2 className="text-2xl md:text-2xl text-gray-900 mb-4 uppercase">
            {config.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {config.subtitle}
          </p>
          {config.showSocialBadge && (
            <div className="mt-4 flex justify-center items-center space-x-4 text-sm text-green-600">
              <span className="flex items-center">✅ 100% Free</span>
              <span className="flex items-center">✅ No Obligation</span>
              <span className="flex items-center">✅ Instant Access</span>
            </div>
          )}
        </div>

        <div className="bg-white shadow-xl rounded-lg px-6 py-8">
          {/* Lead Source Indicator */}
          {isLeadRegistration &&
            leadSourceData &&
            leadSourceData.source !== "direct" && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">Source detected:</span>{" "}
                      {formatLeadSource(leadSourceData)}
                      {leadSourceData.utmData?.campaign && (
                        <span className="ml-2 text-blue-600">
                          • Campaign: {leadSourceData.utmData.campaign}
                        </span>
                      )}
                      {leadSourceData.referralCode && (
                        <span className="ml-2 text-blue-600">
                          • Referral: {leadSourceData.referralCode}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                {...register("name", {
                  required: "Name is required",
                  minLength: {
                    value: 2,
                    message: "Name must be at least 2 characters",
                  },
                })}
                className="input"
                placeholder="Enter your full name"
                autoComplete="name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.name.message}
                </p>
              )}
            </div>

            {/* Username field removed - customers and staff will use email to login */}

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: "Invalid email address",
                  },
                })}
                className="input"
                placeholder="Enter your email"
                autoComplete="email"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            {/* Phone Field */}
            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                {...register("phone", {
                  required: "Phone number is required",
                  pattern: {
                    value: /^[+]?[0-9][\d\s\-\(\)]{7,15}$/,
                    message: "Please enter a valid phone number (8-16 digits)",
                  },
                })}
                className="input"
                placeholder="Enter your phone number"
                autoComplete="tel"
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.phone.message}
                </p>
              )}
            </div>

            {/* WhatsApp Field */}
            <div>
              <label
                htmlFor="whatsapp"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                WhatsApp Number *
              </label>
              <input
                type="tel"
                id="whatsapp"
                {...register("whatsapp", {
                  required: "WhatsApp number is required",
                  pattern: {
                    value: /^[+]?[0-9][\d\s\-\(\)]{7,15}$/,
                    message:
                      "Please enter a valid WhatsApp number (8-16 digits)",
                  },
                })}
                className="input"
                placeholder="Enter your WhatsApp number"
                autoComplete="tel"
              />
              {errors.whatsapp && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.whatsapp.message}
                </p>
              )}
              {isLeadRegistration && (
                <p className="mt-1 text-xs text-gray-500">
                  We'll send you meeting details via WhatsApp
                </p>
              )}
            </div>

            {/* Role Field - for staff registration */}
            {isStaffRegistration && (
              <div>
                <label
                  htmlFor="role"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Role *
                </label>
                <select
                  id="role"
                  {...register("role", {
                    required: "Please select a role",
                  })}
                  className="input"
                >
                  <option value="">Select your role</option>
                  <option value="admin">Admin</option>
                  <option value="sales">Sales Representative</option>
                  <option value="leads">Leads Manager</option>
                  <option value="orders">Orders Manager</option>
                  <option value="telecaller">Telecaller</option>
                </select>
                {errors.role && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.role.message}
                  </p>
                )}
              </div>
            )}

            {/* Password Fields - for user registration only */}
            {config.requirePassword && (
              <>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Password *
                  </label>
                  <input
                    type="password"
                    id="password"
                    {...register("password", {
                      required: "Password is required",
                      minLength: {
                        value: 6,
                        message: "Password must be at least 6 characters",
                      },
                    })}
                    className="input"
                    placeholder="Create a password"
                    autoComplete="new-password"
                  />
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    {...register("confirmPassword", {
                      required: "Please confirm your password",
                      validate: (value) =>
                        value === password || "Passwords do not match",
                    })}
                    className="input"
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>
              </>
            )}

            {/* Benefits Section - for lead registration */}
            {isLeadRegistration && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                <h3 className="text-sm font-semibold text-blue-900 mb-3 flex items-center">
                  🎆 What you'll receive immediately:
                </h3>
                <ul className="text-sm text-blue-800 space-y-2">
                  <li className="flex items-start">
                    <svg
                      className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      <strong>Instant confirmation</strong> via Email & WhatsApp
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      <strong>Quick 3-10 minute video call</strong> with our
                      expert
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      Personalized product recommendations for your needs
                    </span>
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>
                      <strong>Option to book longer consultation</strong> if
                      needed
                    </span>
                  </li>
                </ul>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full font-bold py-4 px-6 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl relative text-lg ${
                isLeadRegistration
                  ? "bg-gradient-to-r from-purple-400 to-purple-800 text-white"
                  : "btn-primary btn-lg"
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="loading-dots mr-2">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  {isLeadRegistration
                    ? "Securing your spot..."
                    : "Creating Account..."}
                </div>
              ) : isLeadRegistration ? (
                <span className="flex items-center justify-center">
                  {config.buttonText}
                  <svg
                    className="w-5 h-5 ml-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
              ) : (
                config.buttonText
              )}
            </button>

            {/* Alternative Options - for lead registration */}
            {isLeadRegistration && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-3">
                  Can't join the quick call?
                </p>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => navigate("/appointments")}
                    className="w-full btn-secondary btn-sm"
                  >
                    Book a Longer Consultation
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/products")}
                    className="w-full btn-secondary btn-sm"
                  >
                    Browse Products & Services
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 text-center">
          {isLeadRegistration ? (
            <>
              <div className="bg-white rounded-lg p-4 shadow-sm border mb-4">
                <div className="flex justify-center items-center space-x-6 text-sm">
                  <div className="flex items-center text-green-600">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    SSL Secured
                  </div>
                  <div className="flex items-center text-blue-600">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    GDPR Compliant
                  </div>
                  <div className="flex items-center text-purple-600">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                        clipRule="evenodd"
                      />
                    </svg>
                    1,000+ Happy Clients
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-1">
                🔒 Your information is 100% secure and will never be shared
              </p>
              <p className="text-xs text-gray-400">
                ✨ Join thousands who discovered our life-changing solutions
              </p>
            </>
          ) : (
            <>
              <p className="text-xs text-gray-500 mb-2">
                🔒 Your information is secure and encrypted
              </p>
              <p className="text-xs text-gray-400">
                {isStaffRegistration
                  ? "👥 Join our professional team at TamilSandhai"
                  : "🎉 Join thousands of satisfied customers"}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadRegistrationPage;
